import { ensureToken } from "./googleAuth";

// The Google Picker lets the user select a file the app did not create. Selecting a
// file grants the app per-file drive.file access to it (the same non-sensitive scope
// MewMaps already uses), so no broader/sensitive scope and no Google OAuth review is
// required. Browsing happens inside the user's own Google session in the Picker UI;
// our token is only handed in so the selected file gets the access grant.
//
// Prerequisites (Google Cloud Console, one-time):
//   - Enable the "Google Picker API" for the project.
//   - Set REACT_APP_GOOGLE_API_KEY to an API key restricted to this app's origin.

const FORM_MIME = "application/vnd.google-apps.form";
const FOLDER_MIME = "application/vnd.google-apps.folder";
const SHEET_MIME = "application/vnd.google-apps.spreadsheet";
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
// setAppId (the Cloud project number) is REQUIRED for the drive.file scope — without it
// the Picker selects the file but the app is never granted access, so later Forms/Drive
// API reads return 403. The OAuth client id is "{projectNumber}-xxxx.apps.googleusercontent.com",
// so the leading numeric segment is the project number.
const APP_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").split("-")[0];
const GAPI_SRC = "https://apis.google.com/js/api.js";

export interface PickedFile {
  id: string;
  name: string;
}

// The Google Picker runs in an iframe served from google.com and needs Google's
// third-party cookies to recognise the signed-in session. Browsers that block third-party
// cookies by default (Firefox ETP, Safari ITP, Brave Shields) make the Picker fail, and
// Google renders a misleading "The API developer key is invalid" message *inside its own
// iframe* — it never rejects our promise, so we can't catch it. The only thing we can do
// is warn affected users with browser-specific steps. Chromium browsers (Chrome/Edge)
// still allow the flow, so they get no hint.
// "generic" covers browsers we can't classify (e.g. iOS Chrome/Firefox, which are WebKit
// and may block, or any lesser-known browser): the Picker might work, so the hint is
// worded conditionally and styled more softly than the known-blocker hints.
export type PickerCookieRiskBrowser = "firefox" | "safari" | "brave" | "generic";

export function detectPickerCookieRiskBrowser(): PickerCookieRiskBrowser | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  // Brave reports a Chrome UA but exposes navigator.brave; its Shields block by default.
  if ((navigator as unknown as { brave?: unknown }).brave) return "brave";
  if (/firefox\//i.test(ua)) return "firefox";
  // Safari (desktop + iOS): WebKit's ITP blocks third-party cookies by default. Exclude
  // Chromium-family UAs and iOS Chrome/Firefox (CriOS/FxiOS), which fall through to generic.
  if (/safari/i.test(ua) && !/chrome|chromium|crios|fxios|android|edg\//i.test(ua)) {
    return "safari";
  }
  // Chromium desktop/Android (Chrome/Edge) allow the Picker flow — no hint. iOS Chrome
  // reports "CriOS" (not "Chrome/"), so it correctly skips this and gets the generic hint.
  if (/chrome\/|edg\//i.test(ua)) return null;
  return "generic";
}

interface PickerDoc {
  id: string;
  name: string;
}
interface PickerResponse {
  action: string;
  docs?: PickerDoc[];
}
interface DocsView {
  setMimeTypes(mimeTypes: string): DocsView;
  setIncludeFolders(include: boolean): DocsView;
  setSelectFolderEnabled(enabled: boolean): DocsView;
  setOwnedByMe(owned: boolean): DocsView;
  setMode(mode: unknown): DocsView;
}
interface Picker {
  setVisible(visible: boolean): void;
}
interface PickerBuilder {
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(appId: string): PickerBuilder;
  setTitle(title: string): PickerBuilder;
  addView(view: DocsView): PickerBuilder;
  setCallback(cb: (data: PickerResponse) => void): PickerBuilder;
  build(): Picker;
}
interface PickerNamespace {
  PickerBuilder: new () => PickerBuilder;
  DocsView: new (viewId?: unknown) => DocsView;
  ViewId: { DOCS: unknown };
  DocsViewMode: { LIST: unknown };
  Action: { PICKED: string; CANCEL: string };
}
interface GapiLoader {
  load(library: string, callback: () => void): void;
}

function pickerWindow(): Window & {
  gapi?: GapiLoader;
  google?: { picker?: PickerNamespace };
} {
  return window as unknown as Window & {
    gapi?: GapiLoader;
    google?: { picker?: PickerNamespace };
  };
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

let pickerReady: Promise<PickerNamespace> | null = null;

function ensurePicker(): Promise<PickerNamespace> {
  if (pickerReady) return pickerReady;
  pickerReady = (async () => {
    await loadScript(GAPI_SRC);
    const w = pickerWindow();
    if (!w.gapi) throw new Error("Google API loader (gapi) is unavailable");
    await new Promise<void>((resolve, reject) => {
      try {
        w.gapi!.load("picker", () => resolve());
      } catch (e) {
        reject(e);
      }
    });
    const picker = pickerWindow().google?.picker;
    if (!picker) throw new Error("Google Picker failed to initialise");
    return picker;
  })().catch((e) => {
    pickerReady = null;
    throw e;
  });
  return pickerReady;
}

// Opens the Google Picker filtered to Google Forms and resolves with the selected
// form, or null if the user cancels. Includes both owned and shared/not-owned forms
// so a publicly shared template from another account can be selected.
export async function pickGoogleForm(title = "Select a form template"): Promise<PickedFile | null> {
  if (!API_KEY) {
    throw new Error("REACT_APP_GOOGLE_API_KEY is not configured. Set it in .env to use the file picker.");
  }
  const token = await ensureToken();
  const picker = await ensurePicker();

  return new Promise<PickedFile | null>((resolve, reject) => {
    try {
      const ownedView = new picker.DocsView(picker.ViewId.DOCS)
        .setMimeTypes(FORM_MIME)
        .setIncludeFolders(false)
        .setOwnedByMe(true)
        .setMode(picker.DocsViewMode.LIST);
      const sharedView = new picker.DocsView(picker.ViewId.DOCS)
        .setMimeTypes(FORM_MIME)
        .setIncludeFolders(false)
        .setOwnedByMe(false)
        .setMode(picker.DocsViewMode.LIST);

      let builder = new picker.PickerBuilder()
        .setOAuthToken(token)
        .setDeveloperKey(API_KEY!)
        .setTitle(title)
        .addView(ownedView)
        .addView(sharedView);
      if (APP_ID) builder = builder.setAppId(APP_ID);
      const built = builder
        .setCallback((data: PickerResponse) => {
          if (data.action === picker.Action.PICKED) {
            const doc = data.docs && data.docs[0];
            resolve(doc ? { id: doc.id, name: doc.name } : null);
          } else if (data.action === picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();
      built.setVisible(true);
    } catch (e) {
      reject(e);
    }
  });
}

// Opens the Google Picker filtered to Google Sheets and resolves with the selected
// spreadsheet, or null if cancelled. Used as the join fallback: a file-pick reliably
// grants the app drive.file access to that exact sheet, whereas selecting a shared
// folder does not always make its existing children listable under drive.file.
export async function pickSpreadsheet(title = "Select the shared mew_dispensers spreadsheet"): Promise<PickedFile | null> {
  if (!API_KEY) {
    throw new Error("REACT_APP_GOOGLE_API_KEY is not configured. Set it in .env to use the file picker.");
  }
  const token = await ensureToken();
  const picker = await ensurePicker();

  return new Promise<PickedFile | null>((resolve, reject) => {
    try {
      const ownedView = new picker.DocsView(picker.ViewId.DOCS)
        .setMimeTypes(SHEET_MIME)
        .setIncludeFolders(false)
        .setOwnedByMe(true)
        .setMode(picker.DocsViewMode.LIST);
      const sharedView = new picker.DocsView(picker.ViewId.DOCS)
        .setMimeTypes(SHEET_MIME)
        .setIncludeFolders(false)
        .setOwnedByMe(false)
        .setMode(picker.DocsViewMode.LIST);

      let builder = new picker.PickerBuilder()
        .setOAuthToken(token)
        .setDeveloperKey(API_KEY!)
        .setTitle(title)
        .addView(ownedView)
        .addView(sharedView);
      if (APP_ID) builder = builder.setAppId(APP_ID);
      const built = builder
        .setCallback((data: PickerResponse) => {
          if (data.action === picker.Action.PICKED) {
            const doc = data.docs && data.docs[0];
            resolve(doc ? { id: doc.id, name: doc.name } : null);
          } else if (data.action === picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();
      built.setVisible(true);
    } catch (e) {
      reject(e);
    }
  });
}

// Opens the Google Picker filtered to Drive folders and resolves with the selected
// folder, or null if the user cancels. Includes both owned and shared/not-owned folders
// so a "MewMaps" folder shared from a manager account (under "Shared with me") can be
// selected — the join flow then reads ownedByMe to decide whether to enter Fieldworker
// mode. Selecting a folder grants the app drive.file access to it (and its contents).
export async function pickDriveFolder(title = "Select the shared MewMaps folder"): Promise<PickedFile | null> {
  if (!API_KEY) {
    throw new Error("REACT_APP_GOOGLE_API_KEY is not configured. Set it in .env to use the file picker.");
  }
  const token = await ensureToken();
  const picker = await ensurePicker();

  return new Promise<PickedFile | null>((resolve, reject) => {
    try {
      const ownedView = new picker.DocsView(picker.ViewId.DOCS)
        .setMimeTypes(FOLDER_MIME)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setOwnedByMe(true)
        .setMode(picker.DocsViewMode.LIST);
      const sharedView = new picker.DocsView(picker.ViewId.DOCS)
        .setMimeTypes(FOLDER_MIME)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setOwnedByMe(false)
        .setMode(picker.DocsViewMode.LIST);

      let builder = new picker.PickerBuilder()
        .setOAuthToken(token)
        .setDeveloperKey(API_KEY!)
        .setTitle(title)
        .addView(ownedView)
        .addView(sharedView);
      if (APP_ID) builder = builder.setAppId(APP_ID);
      const built = builder
        .setCallback((data: PickerResponse) => {
          if (data.action === picker.Action.PICKED) {
            const doc = data.docs && data.docs[0];
            resolve(doc ? { id: doc.id, name: doc.name } : null);
          } else if (data.action === picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();
      built.setVisible(true);
    } catch (e) {
      reject(e);
    }
  });
}
